import { Add } from '@mui/icons-material';
import {
  Checkbox,
  Grid,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Button, Modal, theme } from '@sb/cc-design-system';
import LayoutPanel from 'src/components/layoutPanel';
import { PublicationModalV2 } from 'src/components/modal';
import { StyledTableCellHead } from 'src/components/table/CustomTable.styled';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { TableControlsHeader } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader';
import { ControlType } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import { useModal } from 'src/hooks';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { ILocationListing, PublicationTypes } from 'src/interfaces';
import { lang } from 'src/utils';

import { LocationOwnerTags } from '../components/LocationOwnerTags';
import { RootContainer } from '../LocationListPageOOV2.styled';
import { buildToggleFromConfig } from './BulkEditMessagingPage.helper';
import { useBulkEditMessaging } from './BulkEditMessagingPage.hook';
import {
  HeaderCellContent,
  HeaderStack,
  SummaryBanner,
  TableWrapper,
} from './BulkEditMessagingPage.styled';

export const BulkEditMessagingPage: React.FC = () => {
  const saveConfirmModal = useModal();
  const publishModal = useModal();

  const {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    clientSideTotalPages,
    clientSidePaginatedLocations,
    rowsPerPageItems,
    sortOption,
    setSortOption,
    sortItems,
    isLocationTabDataLoading,
    sortedLocations,
    isSaving,
    toggles,
    initialState,
    summary,
    isDirty,
    bannerHeaderState,
    messagingHeaderState,
    columns,
    locationsContainOutdoorType,
    handleToggleChange,
    handleToggleAll,
    handleSave,
    handleBack,
  } = useBulkEditMessaging();

  useNavigateBlocker(isDirty);

  const handlePublishClose = () => {
    publishModal.onCloseModal();
    handleBack();
  };

  return (
    <RootContainer>
      <LayoutPanel data-testid="layout-panel">
        <LayoutPanel.Header data-testid="layout-header" showBottomBorder>
          <HeaderStack>
            <Button
              variant="outlined-secondary-2"
              size="sm"
              onClick={handleBack}>
              {lang.cancel}
            </Button>
            <Button
              variant="contained-primary"
              size="sm"
              startIcon={<Add />}
              onClick={() => saveConfirmModal.onOpenModal()}
              disabled={!isDirty || isSaving}>
              {lang.save}
            </Button>
          </HeaderStack>
        </LayoutPanel.Header>
        <LayoutPanel.Content
          data-testid="layout-content"
          sx={{ height: 'calc(100% - 48px)' }}>
          <Grid container direction="column" sx={{ height: '100%' }}>
            <Grid
              item
              flex={1}
              sx={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}>
              <TableWrapper>
                <SummaryBanner>
                  <Typography variant="body2" fontWeight={400}>
                    {lang.bulkEditMessagingSummary(
                      summary.banner,
                      summary.messaging,
                    )}
                  </Typography>
                </SummaryBanner>
                <TableControlsHeader
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalCount={sortedLocations.length}
                  selectedSortOption={sortOption}
                  sortItems={sortItems}
                  onSortChange={(option) => setSortOption(option)}
                  onRowsPerPageChange={setRowsPerPage}
                  rowsPerPageItems={rowsPerPageItems}
                  visibleControls={[
                    ControlType.ITEMS_PER_PAGE,
                    ControlType.SORT,
                  ]}
                />

                <CustomTableWithPagination
                  columns={columns}
                  rows={clientSidePaginatedLocations}
                  totalCount={sortedLocations.length}
                  totalPages={clientSideTotalPages}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(_event, value) => setPage(value)}
                  loading={isLocationTabDataLoading}
                  onSortChange={(option) => setSortOption(option)}
                  selectedSortOption={sortOption}
                  showTableControls={false}
                  bodyBackgroundColor={theme.palette.common.white}
                  renderTags={(row: ILocationListing) => (
                    <LocationOwnerTags tags={row.location_owner_tags} />
                  )}
                  tableHeadRenderer={(tableColumns) => (
                    <TableHead>
                      <TableRow sx={{ height: 56 }}>
                        {tableColumns.map((col, idx) => (
                          <StyledTableCellHead
                            key={idx}
                            align={col.align || 'left'}
                            sx={{
                              width: col.width,
                              height: 56,
                              py: 0,
                            }}>
                            {col.id === 'banner' || col.id === 'messaging' ? (
                              <HeaderCellContent>
                                {col.id === 'banner' ? (
                                  <>
                                    <span>{col.label || ''}</span>
                                    <Checkbox
                                      checked={bannerHeaderState.allChecked}
                                      indeterminate={
                                        bannerHeaderState.indeterminate
                                      }
                                      onChange={(event) =>
                                        handleToggleAll(
                                          'banner',
                                          event.target.checked,
                                        )
                                      }
                                      sx={{ p: 0 }}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <Checkbox
                                      checked={messagingHeaderState.allChecked}
                                      indeterminate={
                                        messagingHeaderState.indeterminate
                                      }
                                      onChange={(event) =>
                                        handleToggleAll(
                                          'messaging',
                                          event.target.checked,
                                        )
                                      }
                                      sx={{ p: 0 }}
                                    />
                                    <span>{col.label || ''}</span>
                                  </>
                                )}
                              </HeaderCellContent>
                            ) : (
                              <span>{col.label || ''}</span>
                            )}
                          </StyledTableCellHead>
                        ))}
                      </TableRow>
                    </TableHead>
                  )}
                  customCellRenderer={{
                    banner: (row: ILocationListing) =>
                      locationsContainOutdoorType?.find(
                        (location) =>
                          location.location_code === row.location_code,
                      ) ? (
                        <TableCell key="banner" align="center" sx={{ pl: 15 }}>
                          <Checkbox
                            checked={
                              (
                                toggles[row.location_code] ??
                                initialState[row.location_code] ??
                                buildToggleFromConfig(row.config)
                              ).banner
                            }
                            onChange={(event) =>
                              handleToggleChange(
                                row.location_code,
                                'banner',
                                event.target.checked,
                              )
                            }
                            sx={{ p: 0 }}
                          />
                        </TableCell>
                      ) : (
                        <TableCell key="banner" />
                      ),
                    messaging: (row: ILocationListing) =>
                      locationsContainOutdoorType?.find(
                        (location) =>
                          location.location_code === row.location_code,
                      ) ? (
                        <TableCell
                          key="messaging"
                          align="center"
                          sx={{ pr: 19 }}>
                          <Checkbox
                            checked={
                              (
                                toggles[row.location_code] ??
                                initialState[row.location_code] ??
                                buildToggleFromConfig(row.config)
                              ).messaging
                            }
                            onChange={(event) =>
                              handleToggleChange(
                                row.location_code,
                                'messaging',
                                event.target.checked,
                              )
                            }
                            sx={{ p: 0 }}
                          />
                        </TableCell>
                      ) : (
                        <TableCell key="messaging" />
                      ),
                  }}
                />
              </TableWrapper>
            </Grid>
          </Grid>
        </LayoutPanel.Content>

        {saveConfirmModal.open && (
          <Modal
            open={saveConfirmModal.open}
            title={lang.titleTextSave}
            confirmButtonText={lang.confirm}
            cancelButtonText={lang.cancel}
            onClose={saveConfirmModal.onCloseModal}
            onCancelAction={saveConfirmModal.onCloseModal}
            onConfirmAction={async () => {
              await handleSave();
              saveConfirmModal.onCloseModal();
              publishModal.onOpenModal();
            }}
            sizeVariant="small">
            {lang.subTitlePublishConfirm}
          </Modal>
        )}

        {publishModal.open && (
          <PublicationModalV2
            title={lang.publicationModal.title}
            subTitle={lang.publicationModal.subTitle}
            confirmText={lang.publish}
            cancelText={lang.cancel}
            locationCodes={locationsContainOutdoorType.map(
              (location) => location.location_code,
            )}
            publicationType={PublicationTypes.OVERRIDES}
            open={publishModal.open}
            onClose={handlePublishClose}
            publishMessage={lang.successfullyPublished}
            modalSize="sm"
          />
        )}
      </LayoutPanel>
    </RootContainer>
  );
};
